import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  Backdrop,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLanguage } from '../context/LanguageContext';
import { useTeluguYear } from '../context/TeluguYearContext';
import api from '../utils/api';
import { getGrandhamTreeData, grandhamLabels, grandhamTypeLabels } from '../utils/grandhamTreeUtils';

const PreviousYearExamDetails = () => {
  const { lang } = useLanguage();
  const { teluguYearTelugu, teluguYearEnglish } = useTeluguYear();

  // State for pathasala and vatu list
  const [allVatuList, setAllVatuList] = useState([]);
  const [pathasalaList, setPathasalaList] = useState([]);
  const [selectedPathasala, setSelectedPathasala] = useState('');
  const [filteredVatuList, setFilteredVatuList] = useState([]);
  const [selectedVatuId, setSelectedVatuId] = useState('');
  const [vatuDetails, setVatuDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingVatus, setFetchingVatus] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inactiveYears, setInactiveYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [yearChangeDialog, setYearChangeDialog] = useState(false);
  const [pendingYear, setPendingYear] = useState('');

  // State for grandham selections and marks
  const [krishnaYajurvedaCurrentSelected, setKrishnaYajurvedaCurrentSelected] = useState({});
  const [krishnaYajurvediaPreviousSelected, setKrishnaYajurvediaPreviousSelected] = useState({});
  const [marks, setMarks] = useState({}); // { 'padamu_kanda_pannam': '5' }
  const [expanded, setExpanded] = useState({});

  // Language labels
  const labels = {
    telugu: {
      title: 'గత సంవత్సరం పరీక్ష వివరాలు',
      selectYear: 'సంవత్సరాన్ని ఎంచుకోండి',
      selectPathasala: 'పాఠశాలని ఎంచుకోండి',
      selectVatu: 'విద్యార్థిని ఎంచుకోండి',
      vatuNameLabel: 'విద్యార్థి పేరు',
      pathasalaName: 'పాఠశాల పేరు',
      vatuPhoto: 'విద్యార్థి ఫోటో',
      krishnaCurrentTitle: 'కృష్ణ యజుర్వేదం - ప్రస్తుత పరీక్ష',
      krishnaPreviousTitle: 'కృష్ణ యజుర్వేదం - గత పరీక్ష',
      padamu: 'పదము',
      kramamu: 'క్రమము',
      markLabel: 'మార్కులు (1-10)',
      saveButton: 'సంరక్షించు',
      resetButton: 'రీసెట్ చేయండి',
      enterMarks: 'కృపయా 1-10 మార్కులను నమోదుచేయండి',
      successMessage: 'వివరాలు విజయవంతంగా సంరక్షించబడ్డాయి',
      errorMessage: 'విద్యార్థులను లోడ్ చేయడం విఫలమైంది',
      selectGrandhams: 'కృపయా కనీసం ఒక పాఠం ఎంచుకోండి',
      enterAllMarks: 'ఎంచుకున్న అన్ని పానమ్‌ల కోసం మార్కులను నమోదుచేయండి',
      nothingSelected: 'ఏదీ ఎంచుకోబడలేదు',
      noPhoto: 'ఫోటో లేదు',
      selectOption: 'ఎంచుకోండి',
      yearChangeTitle: 'సంవత్సరం మార్పు',
      yearChangeMessage: 'మీరు సంవత్సరాన్ని మార్చుతున్నారు. ఈ చర్య సన్నిహిత డేటాను రీసెట్ చేస్తుంది. మీరు కొనసాగించాలనుకుంటున్నారా?',
      confirmButton: 'నిశ్చయించు',
      cancelButton: 'రద్దు చేయండి'
    },
    sanskrit: {
      title: 'गतवर्षपरीक्षाविवरणानि',
      selectYear: 'वर्षं वरणयतु',
      selectPathasala: 'पाठशालां वरणयतु',
      selectVatu: 'वटुं वरणयतु',
      vatuNameLabel: 'वटुः नाम',
      pathasalaName: 'पाठशाला नाम',
      vatuPhoto: 'वटुः फोटो',
      krishnaCurrentTitle: 'कृष्ण यजुर्वेदम् - वर्तमान परीक्षा',
      krishnaPreviousTitle: 'कृष्ण यजुर्वेदम् - पूर्व परीक्षा',
      padamu: 'पदम्',
      kramamu: 'क्रमम्',
      markLabel: 'अङ्काः (1-10)',
      saveButton: 'सङ्गृहीतु',
      resetButton: 'रीसेट करें',
      enterMarks: 'कृपया 1-10 अङ्काः प्रविशतु',
      successMessage: 'विवरणानि सफलतया सङ्गृहीतानि',
      errorMessage: 'वटून् आयातनं विफलम्',
      selectGrandhams: 'कृपया कम्पचित् पाठं वरणयतु',
      enterAllMarks: 'सर्वेषु वरितेषु पान्नमु कृते अङ्काः प्रविशतु',
      nothingSelected: 'कदाचित् न वरितम्',
      noPhoto: 'फोटो नहीं',
      selectOption: 'वरणयतु',
      yearChangeTitle: 'वर्षं परिवर्तनम्',
      yearChangeMessage: 'भवान् वर्षं परिवर्तयति। इयं क्रिया वर्तमान डेटा रीसेट करिष्यति। भवान् अग्रसरणं कर्तुम् इच्छति?',
      confirmButton: 'निश्चयितु',
      cancelButton: 'निरसयतु'
    }
  };

  const currentLabels = labels[lang];

  // Get grandham tree data
  const treeData = getGrandhamTreeData('krishna_yajurveda')[lang];

  // Fetch all vatus on component mount
  useEffect(() => {
    const fetchVatus = async () => {
      try {
        setFetchingVatus(true);
        setError('');
        const res = await api.get('/api/vatu/');
        const data = await res.json();

        if (data.success && Array.isArray(data.data)) {
          setAllVatuList(data.data);
          // Extract unique pathasala names
          const uniquePathasalas = [...new Set(data.data.map((v) => v.pathasala_name))];
          setPathasalaList(uniquePathasalas.sort());
        } else {
          setError(currentLabels.errorMessage);
          setAllVatuList([]);
        }
      } catch (err) {
        console.error('Error fetching vatus:', err);
        setError(currentLabels.errorMessage);
        setAllVatuList([]);
      } finally {
        setFetchingVatus(false);
      }
    };

    fetchVatus();
  }, []);

  // Fetch inactive years on component mount
  useEffect(() => {
    const fetchInactiveYears = async () => {
      try {
        const res = await api.get('/api/admin-config/inactive/all');
        const data = await res.json();

        if (data.success && Array.isArray(data.data)) {
          // Extract year names and sort them
          const years = data.data.map((config) => config.teluguYear || config.year);
          setInactiveYears(years.sort());
        }
      } catch (err) {
        console.error('Error fetching inactive years:', err);
      }
    };

    fetchInactiveYears();
  }, []);

  // Handle pathasala selection
  const handlePathasalaSelect = (pathasala) => {
    setSelectedPathasala(pathasala);
    setSelectedVatuId('');
    setVatuDetails(null);
    
    if (pathasala) {
      // Filter vatus by selected pathasala
      const filtered = allVatuList.filter((v) => v.pathasala_name === pathasala);
      setFilteredVatuList(filtered);
    } else {
      setFilteredVatuList([]);
    }

    // Reset selections when pathasala changes
    setKrishnaYajurvedaCurrentSelected({});
    setKrishnaYajurvediaPreviousSelected({});
    setMarks({});
    setExpanded({});
  };

  // Handle vatu selection from dropdown
  const handleVatuSelect = (vatuId) => {
    setSelectedVatuId(vatuId);
    const selected = filteredVatuList.find((v) => v.vatu_id === vatuId);
    
    if (selected) {
      console.log('Selected Vatu Details:', selected);
      console.log('Photo Minio Path:', selected.photo_minio_path);
      console.log('All fields:', Object.keys(selected));
      setVatuDetails(selected);
      // Reset selections when selecting new vatu
      setKrishnaYajurvedaCurrentSelected({});
      setKrishnaYajurvediaPreviousSelected({});
      setMarks({});
      setExpanded({});
    } else {
      setVatuDetails(null);
    }
  };

  // Handle checkbox toggle for grandhams
  const handlePannumToggle = (type, kanda, num, isChecked) => {
    const setSelectorFunc = type === 'current' ? setKrishnaYajurvedaCurrentSelected : setKrishnaYajurvediaPreviousSelected;

    setSelectorFunc((prev) => {
      const newSelected = JSON.parse(JSON.stringify(prev));

      // Initialize structure
      if (!newSelected[padamu]) newSelected[padamu] = {};
      if (!newSelected[padamu][kanda]) newSelected[padamu][kanda] = {};

      const key = `${padamu}_${kanda}_${num}`;

      if (isChecked) {
        newSelected[padamu][kanda][String(num)] = { _selected: true };
        // Initialize marks entry if not exists
        if (!marks[key]) {
          setMarks((prev) => ({ ...prev, [key]: '' }));
        }
      } else {
        delete newSelected[padamu][kanda][String(num)];
        // Remove marks entry
        setMarks((prev) => {
          const newMarks = { ...prev };
          delete newMarks[key];
          return newMarks;
        });
      }

      return newSelected;
    });
  };

  const handleKramumToggle = (type, kanda, num, isChecked) => {
    const setSelectorFunc = type === 'current' ? setKrishnaYajurvedaCurrentSelected : setKrishnaYajurvediaPreviousSelected;

    setSelectorFunc((prev) => {
      const newSelected = JSON.parse(JSON.stringify(prev));

      // Initialize structure
      if (!newSelected[kramamu]) newSelected[kramamu] = {};
      if (!newSelected[kramamu][kanda]) newSelected[kramamu][kanda] = {};

      const key = `${kramamu}_${kanda}_${num}`;

      if (isChecked) {
        newSelected[kramamu][kanda][String(num)] = { _selected: true };
        // Initialize marks entry if not exists
        if (!marks[key]) {
          setMarks((prev) => ({ ...prev, [key]: '' }));
        }
      } else {
        delete newSelected[kramamu][kanda][String(num)];
        // Remove marks entry
        setMarks((prev) => {
          const newMarks = { ...prev };
          delete newMarks[key];
          return newMarks;
        });
      }

      return newSelected;
    });
  };

  // Handle marks input
  const handleMarksChange = (key, value) => {
    const numValue = value.replace(/\D/g, '');
    if (numValue === '' || (parseInt(numValue) >= 1 && parseInt(numValue) <= 10)) {
      setMarks((prev) => ({ ...prev, [key]: numValue }));
    }
  };

  // Validate before save
  const validateSave = () => {
    const currentHasSelection = Object.keys(krishnaYajurvedaCurrentSelected).some(
      (key) => key !== padamu && key !== kramamu && krishnaYajurvedaCurrentSelected[key] && typeof krishnaYajurvedaCurrentSelected[key] === 'object'
    );
    const previousHasSelection = Object.keys(krishnaYajurvediaPreviousSelected).some(
      (key) => key !== padamu && key !== kramamu && krishnaYajurvediaPreviousSelected[key] && typeof krishnaYajurvediaPreviousSelected[key] === 'object'
    );

    if (!currentHasSelection && !previousHasSelection) {
      setError(currentLabels.selectGrandhams);
      return false;
    }

    // Check if all selected pannums have marks
    const selectedPannums = Object.keys(marks);
    for (let key of selectedPannums) {
      if (!marks[key]) {
        setError(currentLabels.enterAllMarks);
        return false;
      }
    }

    return true;
  };

  // Save data to both collections
  const handleSave = async () => {
    if (!validateSave()) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Prepare grandham results
      const grandhamResults = [];

      // Process current grandhams
      if (krishnaYajurvedaCurrentSelected[padamu]) {
        Object.keys(krishnaYajurvedaCurrentSelected[padamu]).forEach((kanda) => {
          if (kanda !== '_selected' && typeof krishnaYajurvedaCurrentSelected[padamu][kanda] === 'object') {
            Object.keys(krishnaYajurvedaCurrentSelected[padamu][kanda]).forEach((num) => {
              const key = `${padamu}_${kanda}_${num}`;
              if (marks[key]) {
                grandhamResults.push({
                  type: padamu,
                  kanda,
                  pannam: num,
                  marks: marks[key],
                  year: 'current'
                });
              }
            });
          }
        });
      }

      if (krishnaYajurvedaCurrentSelected[kramamu]) {
        Object.keys(krishnaYajurvedaCurrentSelected[kramamu]).forEach((kanda) => {
          if (kanda !== '_selected' && typeof krishnaYajurvedaCurrentSelected[kramamu][kanda] === 'object') {
            Object.keys(krishnaYajurvedaCurrentSelected[kramamu][kanda]).forEach((num) => {
              const key = `${kramamu}_${kanda}_${num}`;
              if (marks[key]) {
                grandhamResults.push({
                  type: kramamu,
                  kanda,
                  pannam: num,
                  marks: marks[key],
                  year: 'current'
                });
              }
            });
          }
        });
      }

      // Process previous grandhams
      if (krishnaYajurvediaPreviousSelected[padamu]) {
        Object.keys(krishnaYajurvediaPreviousSelected[padamu]).forEach((kanda) => {
          if (kanda !== '_selected' && typeof krishnaYajurvediaPreviousSelected[padamu][kanda] === 'object') {
            Object.keys(krishnaYajurvediaPreviousSelected[padamu][kanda]).forEach((num) => {
              const key = `${padamu}_${kanda}_${num}`;
              if (marks[key]) {
                grandhamResults.push({
                  type: padamu,
                  kanda,
                  pannam: num,
                  marks: marks[key],
                  year: 'previous'
                });
              }
            });
          }
        });
      }

      if (krishnaYajurvediaPreviousSelected[kramamu]) {
        Object.keys(krishnaYajurvediaPreviousSelected[kramamu]).forEach((kanda) => {
          if (kanda !== '_selected' && typeof krishnaYajurvediaPreviousSelected[kramamu][kanda] === 'object') {
            Object.keys(krishnaYajurvediaPreviousSelected[kramamu][kanda]).forEach((num) => {
              const key = `${kramamu}_${kanda}_${num}`;
              if (marks[key]) {
                grandhamResults.push({
                  type: kramamu,
                  kanda,
                  pannam: num,
                  marks: marks[key],
                  year: 'previous'
                });
              }
            });
          }
        });
      }

      // Save to backend
      const res = await api.post('/api/pariksha-result/previous-year-marks', {
        vatu_id: vatuDetails.vatu_id,
        aadhar: vatuDetails.aadhar,
        vatuName: vatuDetails.vatu_name,
        pathasalaName: vatuDetails.pathasala_name,
        telugu_year: teluguYearTelugu,
        pariksha_type: 'krishna_yajurveda',
        grandhamResults: grandhamResults
      });

      const data = await res.json();

      if (data.success) {
        setSuccess(currentLabels.successMessage);
        // Reset form after successful save
        setTimeout(() => {
          setSelectedPathasala('');
          setSelectedVatuId('');
          setVatuDetails(null);
          setFilteredVatuList([]);
          setKrishnaYajurvedaCurrentSelected({});
          setKrishnaYajurvediaPreviousSelected({});
          setMarks({});
          setExpanded({});
        }, 1500);
      } else {
        setError(data.error || 'Failed to save details');
      }
    } catch (err) {
      console.error('Error saving details:', err);
      setError('Failed to save exam details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Render tree UI for grandhams with hierarchical checkboxes
  const renderGrandhamSection = (type, selected) => {
    const setSelectorFunc = type === 'current' ? setKrishnaYajurvedaCurrentSelected : setKrishnaYajurvediaPreviousSelected;

    // Check if all pannam checkboxes in a kanda are selected
    const isKandaFullySelected = (grandhamType, kanda) => {
      const kandaData = treeData[grandhamType][kanda];
      if (!kandaData || kandaData.length === 0) return false;
      return kandaData.every((num) => selected[grandhamType]?.[kanda]?.[String(num)]?._selected === true);
    };

    // Check if all kandas in a grandham type are selected
    const isGrandhamFullySelected = (grandhamType) => {
      const grandhams = Object.keys(treeData[grandhamType] || {});
      if (grandhams.length === 0) return false;
      return grandhams.every((kanda) => isKandaFullySelected(grandhamType, kanda));
    };

    const handleGrandhamToggle = (e, grandhamType) => {
      e.stopPropagation();
      const isFullySelected = isGrandhamFullySelected(grandhamType);
      
      setSelectorFunc((prev) => {
        const newSelected = JSON.parse(JSON.stringify(prev));
        if (!newSelected[grandhamType]) newSelected[grandhamType] = {};

        Object.keys(treeData[grandhamType]).forEach((kanda) => {
          if (!newSelected[grandhamType][kanda]) newSelected[grandhamType][kanda] = {};
          
          treeData[grandhamType][kanda].forEach((num) => {
            const key = `${grandhamType}_${kanda}_${num}`;
            if (!isFullySelected) {
              newSelected[grandhamType][kanda][String(num)] = { _selected: true };
              if (!marks[key]) {
                setMarks((prev) => ({ ...prev, [key]: '' }));
              }
            } else {
              delete newSelected[grandhamType][kanda][String(num)];
              setMarks((prev) => {
                const newMarks = { ...prev };
                delete newMarks[key];
                return newMarks;
              });
            }
          });
        });
        return newSelected;
      });
    };

    const handleKandaToggle = (e, grandhamType, kanda) => {
      e.stopPropagation();
      const isFullySelected = isKandaFullySelected(grandhamType, kanda);
      
      setSelectorFunc((prev) => {
        const newSelected = JSON.parse(JSON.stringify(prev));
        if (!newSelected[grandhamType]) newSelected[grandhamType] = {};
        if (!newSelected[grandhamType][kanda]) newSelected[grandhamType][kanda] = {};

        treeData[grandhamType][kanda].forEach((num) => {
          const key = `${grandhamType}_${kanda}_${num}`;
          if (!isFullySelected) {
            newSelected[grandhamType][kanda][String(num)] = { _selected: true };
            if (!marks[key]) {
              setMarks((prev) => ({ ...prev, [key]: '' }));
            }
          } else {
            delete newSelected[grandhamType][kanda][String(num)];
            setMarks((prev) => {
              const newMarks = { ...prev };
              delete newMarks[key];
              return newMarks;
            });
          }
        });
        return newSelected;
      });
    };

    const handlePannamToggle = (grandhamType, kanda, num) => {
      setSelectorFunc((prev) => {
        const newSelected = JSON.parse(JSON.stringify(prev));
        if (!newSelected[grandhamType]) newSelected[grandhamType] = {};
        if (!newSelected[grandhamType][kanda]) newSelected[grandhamType][kanda] = {};
        
        const key = `${grandhamType}_${kanda}_${num}`;
        const isCurrentlySelected = newSelected[grandhamType][kanda][String(num)]?._selected === true;

        if (!isCurrentlySelected) {
          newSelected[grandhamType][kanda][String(num)] = { _selected: true };
          if (!marks[key]) {
            setMarks((prev) => ({ ...prev, [key]: '' }));
          }
        } else {
          delete newSelected[grandhamType][kanda][String(num)];
          setMarks((prev) => {
            const newMarks = { ...prev };
            delete newMarks[key];
            return newMarks;
          });
        }
        return newSelected;
      });
    };

    return (
      <Box sx={{ mt: 3 }}>
        {/* Render all grandham types dynamically */}
        {Object.keys(treeData).map((grandhamType) => (
          <Box key={grandhamType} sx={{ mb: 3, border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
            {/* Grandham Type Checkbox */}
            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 2 }} onClick={() => toggleExpand(`${type}_${grandhamType}`)}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isGrandhamFullySelected(grandhamType)}
                    indeterminate={!isGrandhamFullySelected(grandhamType) && Object.keys(selected[grandhamType] || {}).some((kanda) => selected[grandhamType][kanda] && Object.keys(selected[grandhamType][kanda]).length > 0)}
                    onChange={(e) => handleGrandhamToggle(e, grandhamType)}
                  />
                }
                label={<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{grandhamTypeLabels[lang][grandhamType]}</Typography>}
                sx={{ m: 0, flexGrow: 1 }}
              />
              <Box sx={{ ml: 1 }}>{expanded[`${type}_${grandhamType}`] ? <ExpandMoreIcon /> : <ChevronRightIcon />}</Box>
            </Box>

            {/* Kandas and Pannams */}
            {expanded[`${type}_${grandhamType}`] && Object.keys(treeData[grandhamType]).length > 0 && (
              <Box sx={{ ml: 2, mt: 2 }}>
                {Object.keys(treeData[grandhamType]).map((kanda) => (
                  <Box key={kanda} sx={{ mb: 3 }}>
                    {/* Kanda Checkbox */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isKandaFullySelected(grandhamType, kanda)}
                          indeterminate={!isKandaFullySelected(grandhamType, kanda) && treeData[grandhamType][kanda].some((num) => selected[grandhamType]?.[kanda]?.[String(num)]?._selected === true)}
                          onChange={(e) => handleKandaToggle(e, grandhamType, kanda)}
                        />
                      }
                      label={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{kanda}</Typography>}
                      sx={{ m: 0, mb: 1 }}
                    />

                    {/* Pannam checkboxes with textboxes */}
                    <Box sx={{ ml: 3, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
                      {treeData[grandhamType][kanda].map((num) => (
                        <Box key={num} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selected[grandhamType]?.[kanda]?.[String(num)]?._selected === true}
                                onChange={() => handlePannamToggle(grandhamType, kanda, num)}
                              />
                            }
                            label={`${num}${grandhamLabels[lang].pannamu}`}
                            sx={{ m: 0, flexShrink: 0 }}
                          />
                          {selected[grandhamType]?.[kanda]?.[String(num)]?._selected === true && (
                            <TextField
                              type="number"
                              size="small"
                              placeholder="1-10"
                              inputProps={{ min: 1, max: 10, maxLength: 2 }}
                              value={marks[`${grandhamType}_${kanda}_${num}`] || ''}
                              onChange={(e) => handleMarksChange(`${grandhamType}_${kanda}_${num}`, e.target.value)}
                              sx={{ width: '70px', ml: 0.5, flexShrink: 0 }}
                            />
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const toggleExpand = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (fetchingVatus) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 600, color: '#7f4b2d' }}>
          {currentLabels.title}
        </Typography>

        {/* Year Selection Dropdown */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>{currentLabels.selectYear}</InputLabel>
            <Select
              value={selectedYear}
              onChange={(e) => {
                if (e.target.value !== selectedYear) {
                  setPendingYear(e.target.value);
                  setYearChangeDialog(true);
                }
              }}
              label={currentLabels.selectYear}
            >
              <MenuItem value="">
                <em>{currentLabels.selectOption}</em>
              </MenuItem>
              {inactiveYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Year and Pariksha Details Label */}
        <Card sx={{ mb: 3, backgroundColor: '#f5f5f5' }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {lang === 'telugu' ? 'సంవత్సరం' : 'वर्षम्'}: {selectedYear || ""}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {lang === 'telugu' ? 'పరీక్ష' : 'परीक्षा'}: {lang === 'telugu' ? 'కృష్ణ యజుర్వేదం' : 'कृष्ण यजुर्वेदम्'}
            </Typography>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Pathasala Selection Dropdown - Row 1 */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>{currentLabels.selectPathasala}</InputLabel>
            <Select
              value={selectedPathasala}
              onChange={(e) => handlePathasalaSelect(e.target.value)}
              label={currentLabels.selectPathasala}
            >
              <MenuItem value="">
                <em>{currentLabels.selectOption}</em>
              </MenuItem>
              {pathasalaList.map((pathasala) => (
                <MenuItem key={pathasala} value={pathasala}>
                  {pathasala}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Vatu Selection Dropdown - Row 2 */}
        {selectedPathasala && (
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>{currentLabels.selectVatu}</InputLabel>
              <Select
                value={selectedVatuId}
                onChange={(e) => handleVatuSelect(e.target.value)}
                label={currentLabels.selectVatu}
              >
                <MenuItem value="">
                  <em>{currentLabels.selectOption}</em>
                </MenuItem>
                {filteredVatuList.map((vatu) => (
                  <MenuItem key={vatu.vatu_id} value={vatu.vatu_id}>
                    {vatu.vatu_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Vatu Details Display - Row 3 (Pathasala Name + Photo) */}
        {vatuDetails && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 200px' }, gap: 2 }}>
              {/* Pathasala Details */}
              <Card sx={{ backgroundColor: '#e8f5e9' }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    {currentLabels.vatuNameLabel}: {vatuDetails.vatu_name}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {currentLabels.pathasalaName}: {vatuDetails.pathasala_name}
                  </Typography>
                </CardContent>
              </Card>

              {/* Vatu Photo */}
              <Card sx={{ backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <CardContent sx={{ textAlign: 'center', width: '100%' }}>
                  {vatuDetails.photo_download_link ? (
                    <>
                      <Box
                        component="img"
                        src={vatuDetails.photo_download_link}
                        alt={currentLabels.vatuPhoto}
                        sx={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: 1 }}
                        onError={(e) => {
                          console.error('Photo failed to load from download link:', vatuDetails.photo_download_link);
                          e.target.style.display = 'none';
                        }}
                      />
                      <Typography variant="caption" sx={{ fontSize: '10px', color: '#999', mt: 1 }}>
                        Path: {vatuDetails.photo_download_link}
                      </Typography>
                    </>
                  ) : vatuDetails.photo_minio_path ? (
                    <>
                      <Typography variant="caption" sx={{ color: 'orange', mb: 1 }}>
                        (Using MinIO path directly - backend link not available)
                      </Typography>
                      <Box
                        component="img"
                        src={`http://localhost:9000/vatu-documents/${vatuDetails.photo_minio_path.split('/').pop()}`}
                        alt={currentLabels.vatuPhoto}
                        sx={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: 1 }}
                        onError={(e) => {
                          console.error('Photo failed to load from minio path:', vatuDetails.photo_minio_path);
                          e.target.style.display = 'none';
                        }}
                      />
                      <Typography variant="caption" sx={{ fontSize: '10px', color: '#999', mt: 1 }}>
                        MinIO Path: {vatuDetails.photo_minio_path}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {currentLabels.noPhoto} (No photo_download_link or photo_minio_path found)
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}

        {/* Grandham Selection Sections */}
        {vatuDetails && (
          <Box>
            {/* Krishna Yajurveda Current */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
                {currentLabels.krishnaCurrentTitle}
              </Typography>
              {renderGrandhamSection('current', krishnaYajurvedaCurrentSelected)}
            </Box>

            {/* Krishna Yajurveda Previous */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
                {currentLabels.krishnaPreviousTitle}
              </Typography>
              {renderGrandhamSection('previous', krishnaYajurvediaPreviousSelected)}
            </Box>

            {/* Save Button */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading || !vatuDetails}
                sx={{ backgroundColor: '#7f4b2d', px: 4 }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : currentLabels.saveButton}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setSelectedPathasala('');
                  setSelectedVatuId('');
                  setVatuDetails(null);
                  setFilteredVatuList([]);
                  setKrishnaYajurvedaCurrentSelected({});
                  setKrishnaYajurvediaPreviousSelected({});
                  setMarks({});
                  setExpanded({});
                }}
                sx={{ px: 4 }}
              >
                {currentLabels.resetButton}
              </Button>
            </Box>
          </Box>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
      </Paper>

      {/* Year Change Confirmation Dialog */}
      <Dialog open={yearChangeDialog} onClose={() => setYearChangeDialog(false)}>
        <DialogTitle sx={{ fontWeight: 600, color: '#d32f2f' }}>
          {currentLabels.yearChangeTitle}
        </DialogTitle>
        <DialogContent sx={{ minWidth: '400px', mt: 1 }}>
          <Typography variant="body2">
            {currentLabels.yearChangeMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setYearChangeDialog(false)}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            {currentLabels.cancelButton}
          </Button>
          <Button
            onClick={() => {
              setSelectedYear(pendingYear);
              setYearChangeDialog(false);
              // Reset form when year changes
              setSelectedPathasala('');
              setSelectedVatuId('');
              setVatuDetails(null);
              setFilteredVatuList([]);
              setKrishnaYajurvedaCurrentSelected({});
              setKrishnaYajurvediaPreviousSelected({});
              setMarks({});
              setExpanded({});
            }}
            variant="contained"
            sx={{ backgroundColor: '#d32f2f' }}
          >
            {currentLabels.confirmButton}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};

export default PreviousYearExamDetails;
